import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { academicService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Card, Badge, Button, Modal, Input, Select, Field, EmptyState, Spinner, Pagination } from '../../components/ui';
import { getApiErrorMessage } from '../../utils/apiErrors';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_TONE = { pending: 'yellow', approved: 'green', rejected: 'red', expired: 'gray' };
const PAGE_SIZE = 20;

function AdminSponsorships() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [sponsorships, setSponsorships] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ module: '', start_date: todayISO(), end_date: '', order: 0 });
  const [saving, setSaving] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchSponsorships = () => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    academicService.getSponsorships(params)
      .then((res) => {
        const data = res.data;
        setSponsorships(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load sponsorships'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchSponsorships, [statusFilter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    academicService.getModules({ page_size: 100 }).then((res) => {
      const data = res.data;
      setModules(Array.isArray(data) ? data : data.results || []);
    }).catch(() => {});
  }, []);

  const approve = async (sp) => {
    try {
      await academicService.approveSponsorship(sp.id);
      addNotification(t('Sponsorship approved'), 'success');
      fetchSponsorships();
    } catch (e) {
      addNotification(t('Failed to approve sponsorship'), 'error');
    }
  };

  const submitReject = async () => {
    try {
      await academicService.rejectSponsorship(rejectTarget.id, rejectReason);
      addNotification(t('Sponsorship rejected'), 'success');
      setRejectTarget(null);
      setRejectReason('');
      fetchSponsorships();
    } catch (e) {
      addNotification(t('Failed to reject sponsorship'), 'error');
    }
  };

  const createDirect = async () => {
    if (!form.module || !form.start_date || !form.end_date) {
      addNotification(t('Pick a course, start and end date'), 'error');
      return;
    }
    setSaving(true);
    try {
      await academicService.createSponsorship(form);
      addNotification(t('Sponsorship applied'), 'success');
      setCreateOpen(false);
      setForm({ module: '', start_date: todayISO(), end_date: '', order: 0 });
      fetchSponsorships();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to create sponsorship'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Sponsorships')} subtitle={t('Review teacher requests, or feature a course directly')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="sm:max-w-xs">
              <option value="">{t('All statuses')}</option>
              <option value="pending">{t('Pending')}</option>
              <option value="approved">{t('Approved')}</option>
              <option value="rejected">{t('Rejected')}</option>
              <option value="expired">{t('Expired')}</option>
            </Select>
            <Button onClick={() => setCreateOpen(true)}>+ {t('Sponsor a Course Directly')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : sponsorships.length === 0 ? (
            <Card><EmptyState title={t('No sponsorships found')} /></Card>
          ) : (
            <Card padded={false} className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('Course')}</th>
                      <th className="text-left px-4 py-3">{t('Requested By')}</th>
                      <th className="text-left px-4 py-3">{t('Period')}</th>
                      <th className="text-left px-4 py-3">{t('Status')}</th>
                      <th className="text-right px-4 py-3">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sponsorships.map((sp) => (
                      <tr key={sp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{sp.module_name}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {sp.requested_by ? `${sp.requested_by.first_name} ${sp.requested_by.last_name}` : t('Admin (direct)')}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(sp.start_date).toLocaleDateString()} – {new Date(sp.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={STATUS_TONE[sp.status]}>{t(sp.status)}</Badge>
                          {sp.is_current && <Badge tone="blue" className="ms-1">{t('Live now')}</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          {sp.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => approve(sp)}>{t('Approve')}</Button>
                              <Button size="sm" variant="danger" onClick={() => setRejectTarget(sp)}>{t('Reject')}</Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('Sponsor a Course Directly')}
        footer={<>
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={createDirect}>{t('Apply Sponsorship')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Course')} required>
            <Select value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })}>
              <option value="">{t('Select a course')}</option>
              {modules.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.code})</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('Start Date')} required>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </Field>
            <Field label={t('End Date')} required>
              <Input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Display Order')} hint={t('Lower numbers appear first among sponsored courses.')}>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={t('Reject Sponsorship Request')}
        footer={<>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>{t('Cancel')}</Button>
          <Button variant="danger" onClick={submitReject}>{t('Reject')}</Button>
        </>}
      >
        <Field label={t('Reason (optional)')}>
          <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </Field>
      </Modal>
    </div>
  );
}

export default AdminSponsorships;
